/**
 * PHOTO-TO-3D round (docs/bim-plan/17 §3.2): the `mesh.bin` asset format — the ONE
 * artifact the geometry worker consumes for an imported (photo/manufacturer) mesh.
 *
 * Our own compact typed-array container, deliberately NOT GLB: the worker never
 * needs GLTFLoader, three.js, or any decoder — it fetches one blob and hands the
 * buffers straight to the existing `MeshBuffers`/`OpeningProto` channel. GLB stays
 * the vendor interchange format only (`raw/<jobId>.glb` + the textured
 * `lib/<assetId>/mesh.glb` kept in Storage for provenance/re-bake).
 *
 * Layout (little-endian):
 *   u32 magic 'AJM1' (0x314d4a41)  u32 version (1)
 *   u32 vertCount  u32 indexCount  u32 edgeFloatCount  u32 flags (bit0 = has uvs)
 *   f32[vertCount*3]  positions   (object local frame: anchor back-center,
 *                                  x centered, +y front, z up from 0 — §4.3)
 *   f32[vertCount*3]  normals
 *   f32[vertCount*2]  uvs         (present iff flags bit0 — doc-17 Q2 resolution:
 *                                  textured from day one, vendor UVs preserved
 *                                  through index-remap decimation)
 *   u32[indexCount]   indices
 *   f32[edgeFloatCount] featureEdges (xyzxyz line-segment pairs — PRE-BAKED at
 *                                  intake so the worker never recomputes them)
 *
 * Everything is 4-byte aligned by construction (all fields are 4-byte types).
 */

export const MESH_BIN_MAGIC = 0x314d4a41   // 'AJM1'
export const MESH_BIN_VERSION = 1

export interface MeshAsset {
  positions: Float32Array
  normals: Float32Array
  /** vendor UVs, preserved through decimation — absent for a UV-less source */
  uvs: Float32Array | null
  indices: Uint32Array
  /** featureEdges() output baked at intake — xyzxyz segment pairs */
  edges: Float32Array
}

export function encodeMeshBin(asset: MeshAsset): ArrayBuffer {
  const vertCount = asset.positions.length / 3
  const hasUvs = !!asset.uvs && asset.uvs.length === vertCount * 2
  const headerBytes = 6 * 4
  const bytes = headerBytes
    + asset.positions.byteLength
    + asset.normals.byteLength
    + (hasUvs ? asset.uvs!.byteLength : 0)
    + asset.indices.byteLength
    + asset.edges.byteLength
  const buf = new ArrayBuffer(bytes)
  const dv = new DataView(buf)
  dv.setUint32(0, MESH_BIN_MAGIC, true)
  dv.setUint32(4, MESH_BIN_VERSION, true)
  dv.setUint32(8, vertCount, true)
  dv.setUint32(12, asset.indices.length, true)
  dv.setUint32(16, asset.edges.length, true)
  dv.setUint32(20, hasUvs ? 1 : 0, true)
  let off = headerBytes
  new Float32Array(buf, off, asset.positions.length).set(asset.positions)
  off += asset.positions.byteLength
  new Float32Array(buf, off, asset.normals.length).set(asset.normals)
  off += asset.normals.byteLength
  if (hasUvs) {
    new Float32Array(buf, off, asset.uvs!.length).set(asset.uvs!)
    off += asset.uvs!.byteLength
  }
  new Uint32Array(buf, off, asset.indices.length).set(asset.indices)
  off += asset.indices.byteLength
  new Float32Array(buf, off, asset.edges.length).set(asset.edges)
  return buf
}

/** Throws on a wrong magic/version/short buffer — a corrupt asset must fail loud at
 *  registration time, never render garbage silently. */
export function decodeMeshBin(buf: ArrayBuffer): MeshAsset {
  if (buf.byteLength < 24) throw new Error('mesh.bin: truncated header')
  const dv = new DataView(buf)
  if (dv.getUint32(0, true) !== MESH_BIN_MAGIC) throw new Error('mesh.bin: bad magic')
  const version = dv.getUint32(4, true)
  if (version !== MESH_BIN_VERSION) throw new Error(`mesh.bin: unsupported version ${version}`)
  const vertCount = dv.getUint32(8, true)
  const indexCount = dv.getUint32(12, true)
  const edgeFloatCount = dv.getUint32(16, true)
  const hasUvs = (dv.getUint32(20, true) & 1) === 1
  let off = 24
  const need = vertCount * 3 * 4 * 2 + (hasUvs ? vertCount * 2 * 4 : 0) + indexCount * 4 + edgeFloatCount * 4
  if (buf.byteLength < off + need) throw new Error('mesh.bin: truncated body')
  const positions = new Float32Array(buf, off, vertCount * 3); off += positions.byteLength
  const normals = new Float32Array(buf, off, vertCount * 3); off += normals.byteLength
  let uvs: Float32Array | null = null
  if (hasUvs) { uvs = new Float32Array(buf, off, vertCount * 2); off += uvs.byteLength }
  const indices = new Uint32Array(buf, off, indexCount); off += indices.byteLength
  const edges = new Float32Array(buf, off, edgeFloatCount)
  return { positions, normals, uvs, indices, edges }
}

// ---------------------------------------------------------------------------
// The MESH REGISTRY (doc 17 §4.5): worker-side cache of decoded assets keyed by
// meshRef (`lib/<assetId>`). Registered once per session via the worker API's
// registerMeshAssets; buildObjectProto consults it. Assets are immutable once
// approved (a re-bake mints a NEW assetId → new meshRef → protoKeyFor forks), so
// there is no invalidation story — the map only ever grows within a session.
// Module-level singleton: this module is instantiated once per JS realm, so the
// worker has its own registry and (for previews) the main thread has its own.
// ---------------------------------------------------------------------------

const REGISTRY = new Map<string, MeshAsset>()

export function registerMeshAsset(meshRef: string, asset: MeshAsset): void {
  if (!meshRef) return
  REGISTRY.set(meshRef, asset)
}

export function getMeshAsset(meshRef: string | undefined | null): MeshAsset | null {
  if (!meshRef) return null
  return REGISTRY.get(meshRef) ?? null
}

export function registeredMeshRefs(): string[] {
  return [...REGISTRY.keys()]
}
